/**
 * useDialpadSms
 *
 * Fetches SMS thread for a phone number and sends SMS via the smsProxy
 * Cloud Function. Uses React Query for caching and invalidation.
 *
 * @example
 * const { messages, isLoading, sendSms, isSending } = useDialpadSms('+12095551234', 'Tracy');
 *
 * @param phoneNumber - External phone number (E.164 or raw digits)
 * @param branch - Branch name for default send-from number
 * @returns SMS thread state and send action
 */

import { blockedSmsMessage, duplicateSmsMessage } from './smsBlockedMessage';
import { useState, useEffect } from 'react';
import { useMutation, type MutateOptions } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { httpsCallable } from '../../lib/firebase/authReadyCallable';
import {
  collection, query, orderBy, onSnapshot, limit,
} from 'firebase/firestore';
import { subscribeWithTransientRetry, type QuerySnap } from '../../lib/support/listenerRetry';
import { db, functions } from '../../config/firebase';
import { useOrganization } from '../../contexts/OrganizationContext';
import { getBranchSmsNumber } from '../../config/dialpadBranches';
import { describeCallableFailure } from '../../lib/offlineErrors';
import { logCallableFailure } from '../../lib/logCallableFailure';

// ============================================================================
// Types
// ============================================================================

export interface SmsMessage {
  id: string;
  /** Alias for id - used by some components */
  messageId: string;
  text: string;
  /** Alias for text - used by some components */
  body: string;
  timestamp: string | { toDate: () => Date };
  direction: 'inbound' | 'outbound';
  fromNumber: string;
  toNumber: string;
  /** Name of user who sent (for outbound messages) */
  sentByName?: string;
}

interface SmsProxySendResponse {
  success: boolean;
}

interface SendSmsParams {
  text: string;
  branch: string;
}

// ============================================================================
// Query Keys
// ============================================================================

export const dialpadSmsKeys = {
  all: ['dialpadSms'] as const,
  thread: (phoneNumber: string) => [...dialpadSmsKeys.all, 'thread', phoneNumber] as const,
};

// ============================================================================
// Cloud Function callables
// ============================================================================

const GENERIC_SEND_FAILURE = 'Failed to send SMS. Please try again.';
const NO_ANSWER_SEND_MESSAGE =
  "No answer came back, so we can't tell whether the text was sent. Check the conversation before sending it again.";

const smsProxyFn = httpsCallable<
  { action: string; phoneNumber: string; text?: string; branch?: string },
  SmsProxySendResponse
>(functions, 'smsProxy');

// ============================================================================
// Hook
// ============================================================================

export interface UseDialpadSmsResult {
  /** SMS messages sorted oldest-first */
  messages: SmsMessage[];
  /** True during initial load */
  isLoading: boolean;
  /** Send an SMS message */
  sendSms: (params: SendSmsParams, options?: MutateOptions<SmsProxySendResponse, Error, SendSmsParams>) => void;
  /** Same send, but settles with the outcome (rejects on failure; the failure message is already shown). */
  sendSmsAsync: (params: SendSmsParams) => Promise<SmsProxySendResponse>;
  /** True while a send is in progress */
  isSending: boolean;
}

export function useDialpadSms(
  phoneNumber: string | undefined,
  defaultBranch: string
): UseDialpadSmsResult {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.organizationId;

  // Normalize phone to last 10 digits for Firestore path
  const normalizedPhone = phoneNumber
    ? phoneNumber.replace(/\D/g, '').slice(-10)
    : '';

  // Messages state — populated solely from Firestore listener
  const [messages, setMessages] = useState<SmsMessage[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Firestore listener — sole data source for SMS thread
  useEffect(() => {
    if (!normalizedPhone || normalizedPhone.length < 10 || !orgId) {
      setMessages([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const messagesRef = collection(
      db,
      'organizations', orgId,
      'smsThreads', normalizedPhone,
      'messages',
    );
    const q = query(messagesRef, orderBy('createdAt', 'desc'), limit(50));

    const unsub = subscribeWithTransientRetry<QuerySnap>({
      label: 'dialpadSms',
      subscribe: (onData, onError) => onSnapshot(q, onData, onError),
      onData: (snap) => {
      const msgs: SmsMessage[] = snap.docs.map(doc => {
        const d = doc.data();
        const id = d.id || doc.id;
        const text = d.text || '';
        return {
          id,
          messageId: id,
          text,
          body: text,
          timestamp: d.createdAt || d.timestamp || '',
          direction: d.direction || 'inbound',
          fromNumber: d.fromNumber || '',
          toNumber: d.toNumber || '',
        };
      });
      // Reverse for oldest-first chat display (query is desc for "most recent 50")
      setMessages(msgs.reverse());
      setIsLoading(false);
      },
      onError: (err) => {
        // eslint-disable-next-line no-restricted-syntax -- Firestore snapshot listener, not a callable
        console.error('[useDialpadSms] Firestore listener error:', err);
        setIsLoading(false);
      },
    });

    return unsub;
  }, [normalizedPhone, orgId]);

  // Send SMS mutation
  const sendMutation = useMutation({
    mutationFn: async (params: SendSmsParams) => {
      if (!phoneNumber) {
        throw new Error('No phone number available');
      }
      const branch = params.branch || defaultBranch;
      if (!branch) {
        throw new Error('NO_BRANCH');
      }
      const result = await smsProxyFn({
        action: 'send',
        phoneNumber,
        text: params.text,
        branch,
      });
      return result.data as SmsProxySendResponse;
    },
    onMutate: async (params) => {
      // Optimistic: add outbound message to local state immediately
      const fromNumber = getBranchSmsNumber(params.branch || defaultBranch) || '';
      const optimisticId = `optimistic-${Date.now()}`;
      const optimisticMsg: SmsMessage = {
        id: optimisticId,
        messageId: optimisticId,
        text: params.text,
        body: params.text,
        timestamp: new Date().toISOString(),
        direction: 'outbound',
        fromNumber,
        toNumber: phoneNumber || '',
      };
      const previousMessages = [...messages];
      setMessages(prev => [...prev, optimisticMsg]);
      return { previousMessages };
    },
    onError: (err, _vars, context) => {
      if (context?.previousMessages) {
        setMessages(context.previousMessages);
      }
      const blocked = blockedSmsMessage(err);
      const duplicate = duplicateSmsMessage(err);
      if (err instanceof Error && err.message === 'NO_BRANCH') {
        toast.error('No branch configured -- cannot send SMS');
      } else if (blocked) {
        toast.error(blocked, { duration: 10000 });
      } else if (duplicate) {
        toast.error(duplicate, { duration: 12000 });
      } else {
        logCallableFailure('[useDialpadSms] SMS send failed:', err);
        // No answer from the server (a timeout the server did not label): the text may well have gone, and "please try
        // again" is what makes someone text a customer twice. Say it plainly, the way the labelled "Not confirmed" does.
        const failure = describeCallableFailure(err, GENERIC_SEND_FAILURE, undefined, { workKeptOnDevice: false });
        if (failure.message !== GENERIC_SEND_FAILURE) {
          toast.error(NO_ANSWER_SEND_MESSAGE, { duration: 12000 });
        } else {
          toast.error(failure.message);
        }
      }
    },
    // No onSettled re-fetch — Firestore onSnapshot handles sync automatically
  });

  return {
    messages,
    isLoading,
    sendSms: sendMutation.mutate,
    sendSmsAsync: sendMutation.mutateAsync,
    isSending: sendMutation.isPending,
  };
}
